import React from "react";
import {
  Dialog,
  Portal,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { FiX, FiCalendar, FiMapPin, FiUsers, FiDollarSign, FiBriefcase } from "react-icons/fi";

const CorporateDetailsModal = ({ isOpen, onClose, corporateData }) => {
  if (!corporateData) return null;

  const { companyInfo, email, documents, status } = corporateData;
  const { basic, regulatory, financial } = companyInfo || {};

  return (
    <Dialog.Root open={isOpen} size="3xl" placement="center" motionPreset="scale">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="80vh" overflowY="auto">
            <Dialog.Header>
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="xl" fontWeight="bold">
                  Corporate Details
                </Text>
                <Button size="sm" variant="ghost" onClick={onClose}>
                  <FiX />
                </Button>
              </HStack>
            </Dialog.Header>

            <Dialog.Body>
              <VStack spacing={6} align="stretch">
                {/* Status Badge */}
                <Box>
                  <Badge
                    colorScheme={
                      status === "approved" 
                        ? "green" 
                        : status === "rejected" 
                        ? "red" 
                        : "orange"
                    }
                    fontSize="md"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {status || "pending_verification"}
                  </Badge>
                </Box>

                {/* Basic Information Section */}
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4} color="blue.600">
                    <FiBriefcase style={{ display: "inline", marginRight: "8px" }} />
                    Basic Information
                  </Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Company Name</Text>
                      <Text fontSize="md">{basic?.corporateName || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Email</Text>
                      <Text fontSize="md">{email || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Incorporation Type</Text>
                      <Text fontSize="md">{basic?.typeOfIncorporation || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Ownership Type</Text>
                      <Text fontSize="md">{basic?.ownershipType || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Country</Text>
                      <Text fontSize="md">{basic?.countryOfIncorporation || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Mobile</Text>
                      <Text fontSize="md">{basic?.mobile || "N/A"}</Text>
                    </GridItem>
                    <GridItem colSpan={2}>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        <FiMapPin style={{ display: "inline", marginRight: "8px" }} />
                        Registered Address
                      </Text>
                      <Text fontSize="md">{basic?.registeredAddress || "N/A"}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                {/* Custom Divider */}
                <Box height="1px" bg="gray.200" my={4} />

                {/* Incorporation Details */}
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4} color="green.600">
                    <FiCalendar style={{ display: "inline", marginRight: "8px" }} />
                    Incorporation Details
                  </Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Incorporation Date</Text>
                      <Text fontSize="md">
                        {basic?.incorporationDate 
                          ? new Date(basic.incorporationDate).toLocaleDateString()
                          : "N/A"
                        }
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Incorporation Number</Text>
                      <Text fontSize="md">{basic?.incorporationNumber || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        <FiUsers style={{ display: "inline", marginRight: "8px" }} />
                        Number of Shareholders
                      </Text>
                      <Text fontSize="md">{basic?.numberOfShareholders || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        <FiUsers style={{ display: "inline", marginRight: "8px" }} />
                        Number of Staff
                      </Text>
                      <Text fontSize="md">{basic?.numberOfStaff || "N/A"}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                {/* Custom Divider */}
                <Box height="1px" bg="gray.200" my={4} />

                {/* Financial Information */}
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4} color="purple.600">
                    <FiDollarSign style={{ display: "inline", marginRight: "8px" }} />
                    Financial Information
                  </Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Annual Turnover</Text>
                      <Text fontSize="md">{financial?.annualTurnover || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Net Assets</Text>
                      <Text fontSize="md">{financial?.netAssets || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Source of Wealth</Text>
                      <Text fontSize="md">{financial?.sourceOfWealth || "N/A"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Tax Number</Text>
                      <Text fontSize="md">{financial?.corporateTaxNumber || "N/A"}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                {/* Custom Divider */}
                <Box height="1px" bg="gray.200" my={4} />

                {/* Regulatory Information */}
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4} color="orange.600">
                    Regulatory Information
                  </Text>
                  <Grid templateColumns="1fr" gap={4}>
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Main Business Activity</Text>
                      <Text fontSize="md">{regulatory?.mainBusinessActivity || "N/A"}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                {/* Documents Summary */}
                {documents && documents.length > 0 && (
                  <>
                    {/* Custom Divider */}
                    <Box height="1px" bg="gray.200" my={4} />
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold" mb={4} color="teal.600">
                        Documents
                      </Text>
                      <VStack spacing={3} align="stretch">
                        {documents.map((doc, index) => (
                          <Box key={doc._id || index} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                            <Text fontWeight="medium">{doc.type}</Text>
                            <Text fontSize="sm" color="gray.600">
                              Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : "N/A"}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              Status: <Badge colorScheme={doc.status === "approved" ? "green" : "orange"} variant="subtle">
                                {doc.status || "pending"}
                              </Badge>
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </>
                )}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CorporateDetailsModal;